import { first, isEmpty } from 'underscore';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link, Callout, DirectionalHint, getId } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getDisclaimerStyles } from '../styles';
import type { UAPI18n, UAPPlacementComponentProps } from '../@types';

type DisclaimerProps = Pick<UAPPlacementComponentProps, 'disclaimer' | 'i18n' | 'styleOverrides'>;
const i18nPropType = PropTypes.shape({
  getString: PropTypes.func.isRequired,
}) as PropTypes.Requireable<UAPI18n>;

export const AdditionalLineDisclaimer = React.forwardRef<HTMLAnchorElement, DisclaimerProps>(({

  i18n,

  disclaimer,

  styleOverrides,
}, ref) => {
  const classes = useDefaultStyles(getDisclaimerStyles({ styleOverrides }));

  return !isEmpty(disclaimer) && disclaimer.DisclaimerLayout === 'AdditionalLine' ? (
    <Link
      className={classnames('disclaimer-url', classes.additionalLineDisclaimer)}

      ref={ref}
      target="_blank"
      href={first(disclaimer.FinalUrls)}
    >
      {i18n.getString(_TL_('More Info'))}
    </Link>
  ) : null;
});

AdditionalLineDisclaimer.propTypes = {

  i18n: i18nPropType.isRequired,
  disclaimer: PropTypes.shape({
    DisclaimerLayout: PropTypes.string,
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
  }),
  styleOverrides: PropTypes.shape({}),
};

AdditionalLineDisclaimer.defaultProps = {

  disclaimer: {},
  styleOverrides: {},
};

export const PopupDisclaimer = ({
  i18n,
  disclaimer,
  styleOverrides,
}: UAPPlacementComponentProps) => {
  const classes = useDefaultStyles(getDisclaimerStyles({ styleOverrides }));
  const [showCallout, setShowCallout] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);

  return !isEmpty(disclaimer) && disclaimer.DisclaimerLayout === 'Popup' ? (
    <div className={classnames('popup-disclaimer', classes.popupDisclaimer)}>
      <button
        className="disclaimer-title"
        onClick={() => setShowCallout(!showCallout)}
        ref={ref}
      >
        {disclaimer.Title}
      </button>
      {showCallout &&
        <Callout
          className={classnames('disclaimer-callout', classes.disclaimerCallout)}
          ariaLabelledBy={getId('disclaimer-title')}
          ariaDescribedBy={getId('disclaimer-description')}
          target={ref && ref.current}
          directionalHint={DirectionalHint.bottomLeftEdge}
          gapSpace={0}
          isBeakVisible={false}
          setInitialFocus
          onDismiss={() => setShowCallout(false)}
        >
          <button className="close" aria-label={i18n.getString(_TL_('Close'))} onClick={() => setShowCallout(false)} >
            <span className="iconba iconba-Cancel" />
          </button>
          <div className="title" id={getId('disclaimer-title')}>
            {i18n.getString(_TL_('Disclaimer'))}
          </div>
          <div className="content" id={getId('disclaimer-description')}>
            {disclaimer.PopupText}
          </div>
          <Link className="disclaimer-url" disabled={isEmpty(disclaimer.FinalUrls)} href={first(disclaimer.FinalUrls) || ''} target="_blank">
            {i18n.getString('Learn more')}
          </Link>
        </Callout>
      }
    </div>
  ) : null;
};

PopupDisclaimer.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  disclaimer: PropTypes.shape({
    DisclaimerLayout: PropTypes.string,
    Title: PropTypes.string,
    PopupText: PropTypes.string,
    FinalUrls: PropTypes.arrayOf(PropTypes.string),
  }),
  styleOverrides: PropTypes.shape({}),
};

PopupDisclaimer.defaultProps = {
  disclaimer: {},
  styleOverrides: {},
};
